import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';
import {
  Hackday,
  UserProfile,
  Bounty,
  CreateHackdayForm,
  CreateBountyForm,
  ApiResponse,
  HackdayFilters,
  Notification
} from '../types/context';
import { cacheManager, CACHE_TTL } from '../utils/cache';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api/v1';

// Define cache invalidation relationships
// When a mutation happens on an endpoint, these related patterns should also be invalidated
const CACHE_RELATIONSHIPS: Record<string, string[]> = {
  'team-registration': ['current-events-bounties', 'user-events-bounties', 'currentprojects', 'hackdays', 'teams', 'currentattendees', 'teammanagement', 'platformoverview'],
  'teams': ['current-events-bounties', 'user-events-bounties', 'currentprojects', 'hackdays', 'currentattendees', 'teammanagement', 'platformoverview'],
  'participations': ['current-events-bounties', 'user-events-bounties', 'hackdays', 'platformoverview'],
  'hackdays': ['current-events-bounties', 'user-events-bounties', 'currentprojects', 'platformoverview', 'sponsor-next-event'],
  'bounties': ['current-events-bounties', 'user-events-bounties', 'current-sponsor-bounties', 'platformoverview'],
  'current-events-bounties': ['user-events-bounties', 'hackdays', 'bounties'],
  'teammanagement': ['teams', 'currentattendees', 'currentprojects'],
  'notifications': ['notifications'], // Self-invalidation for notification updates
  'connect': ['payouts'], // Stripe connected accounts affect payouts
  'payouts': ['connect'], // Payouts affect connected account balance
};

// API client class
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Cache management methods
  clearCache(): void {
    cacheManager.clear();
  }

  invalidateCache(pattern: string): void {
    // Clear in-memory cache
    cacheManager.invalidatePattern(pattern);

    // Clear related ETags from localStorage
    const patternsToInvalidate = [pattern, ...(CACHE_RELATIONSHIPS[pattern] || [])];

    patternsToInvalidate.forEach(p => {
      try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('etag:') && key.includes(p)) {
            localStorage.removeItem(key);
          }
        });
      } catch (err) {
        console.error('Failed to clear ETags:', err);
      }
    });
  }

  async prefetchData<T>(
    endpoint: string,
    getToken?: () => Promise<string | null>
  ): Promise<void> {
    const cacheKey = cacheManager.generateKey('api', endpoint);
    await cacheManager.prefetch(
      cacheKey,
      () => this.request<T>(endpoint, { getToken }),
      { ttl: CACHE_TTL.MEDIUM }
    );
  }

  // Helper method to get auth headers
  private async getAuthHeaders(getToken: () => Promise<string | null>): Promise<HeadersInit> {
    const token = await getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Generic request method with caching and ETag support
  private async request<T>(
    endpoint: string,
    options: RequestInit & {
      getToken?: () => Promise<string | null>;
      cache?: boolean;
      cacheTTL?: number;
    } = {}
  ): Promise<ApiResponse<T>> {
    const { getToken, cache = true, cacheTTL = CACHE_TTL.MEDIUM, ...requestOptions } = options;
    const url = `${this.baseURL}${endpoint}`;
    const method = requestOptions.method || 'GET';

    // Only cache GET requests
    const shouldCache = cache && method === 'GET';
    const cacheKey = shouldCache ? cacheManager.generateKey('api', endpoint) : '';
    const etagKey = `etag:${endpoint}`;

    const fetchFn = async (): Promise<ApiResponse<T>> => {
      try {
        const baseHeaders = getToken
          ? await this.getAuthHeaders(getToken)
          : { 'Content-Type': 'application/json' };

        // Add ETag for conditional requests (if cached)
        const cachedEtag = shouldCache ? localStorage.getItem(etagKey) : null;
        const headers: HeadersInit = { ...baseHeaders };
        if (cachedEtag) {
          (headers as Record<string, string>)['If-None-Match'] = cachedEtag;
        }

        const response = await fetch(url, {
          ...requestOptions,
          headers: {
            ...headers,
            ...requestOptions.headers,
          },
        });

        // Handle 304 Not Modified - return cached data
        if (response.status === 304 && shouldCache) {
          const cached = cacheManager.get<ApiResponse<T>>(cacheKey);
          if (cached) {
            return cached;
          }
        }

        const data = await response.json();

        if (!response.ok) {
          return {
            success: false,
            error: data.error || 'Request failed',
            message: data.message || `HTTP ${response.status}`,
          };
        }

        // Store ETag for future requests
        const etag = response.headers.get('etag');
        if (etag && shouldCache) {
          localStorage.setItem(etagKey, etag);
        }

        return data;
      } catch (error) {
        console.error('API request failed:', error);
        return {
          success: false,
          error: 'Network error',
          message: error instanceof Error ? error.message : 'Request failed',
        };
      }
    };

    // Use cache for GET requests
    if (shouldCache) {
      return cacheManager.fetchWithCache(
        cacheKey,
        fetchFn,
        { ttl: cacheTTL, staleWhileRevalidate: true }
      );
    }

    // For non-GET requests, invalidate related cache
    const result = await fetchFn();
    if (result.success && ['POST', 'PUT', 'DELETE'].includes(method)) {
      // Invalidate cache for this endpoint pattern and all related patterns
      const pattern = endpoint.split('/')[1] || endpoint;
      const patternsToInvalidate = [pattern, ...(CACHE_RELATIONSHIPS[pattern] || [])];

      patternsToInvalidate.forEach(p => {
        cacheManager.invalidatePattern(p);
        // Clear related ETags
        try {
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.startsWith('etag:') && key.includes(p)) {
              localStorage.removeItem(key);
            }
          });
        } catch { }
      });
    }

    return result;
  }

  // Hackday endpoints
  async getHackdays(
    filters?: HackdayFilters & { page?: number; limit?: number },
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{ hackdays: Hackday[]; pagination: { page: number; limit: number; total: number; pages: number; hasNext: boolean; hasPrev: boolean } }>> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const queryString = params.toString();
    const endpoint = `/hackdays${queryString ? `?${queryString}` : ''}`;

    return this.request<{ hackdays: Hackday[]; pagination: { page: number; limit: number; total: number; pages: number; hasNext: boolean; hasPrev: boolean } }>(endpoint, { getToken });
  }

  async getHackday(
    id: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<Hackday>> {
    return this.request<Hackday>(`/hackdays/${id}`, { getToken });
  }

  async createHackday(
    data: CreateHackdayForm,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Hackday>> {
    return this.request<Hackday>('/hackdays', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async updateHackday(
    id: string,
    data: Partial<CreateHackdayForm>,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Hackday>> {
    return this.request<Hackday>(`/hackdays/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async deleteHackday(
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/hackdays/${id}`, {
      method: 'DELETE',
      getToken,
    });
  }

  async getSponsorHackdays(
    sponsorId: string = 'me',
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Hackday[]>> {
    return this.request<Hackday[]>(`/hackdays/sponsor/${sponsorId}`, { getToken });
  }


  // User endpoints
  async getCurrentUser(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<UserProfile>> {
    return this.request<UserProfile>('/users/me', { getToken });
  }

  async updateCurrentUser(
    data: { username?: string; role?: 'sponsor' | 'participant' },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<UserProfile>> {
    return this.request<UserProfile>('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async getUserStats(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/users/me/stats', { getToken });
  }

  async getUserActivity(
    page: number = 1,
    limit: number = 10,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/users/me/activity?page=${page}&limit=${limit}`, { getToken });
  }

  // Bounty endpoints
  async getBounties(
    filters?: any & { page?: number; limit?: number },
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{ bounties: Bounty[]; pagination: { page: number; limit: number; total: number; pages: number; hasNext: boolean; hasPrev: boolean } }>> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const queryString = params.toString();
    const endpoint = `/bounties${queryString ? `?${queryString}` : ''}`;

    return this.request<{ bounties: Bounty[]; pagination: { page: number; limit: number; total: number; pages: number; hasNext: boolean; hasPrev: boolean } }>(endpoint, { getToken });
  }

  async getBounty(
    id: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<Bounty>> {
    return this.request<Bounty>(`/bounties/${id}`, { getToken });
  }

  async getHackdayBounties(
    hackdayId: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<Bounty[]>> {
    return this.request<Bounty[]>(`/bounties/hackday/${hackdayId}`, { getToken });
  }

  async createBounty(
    data: CreateBountyForm,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Bounty>> {
    return this.request<Bounty>('/bounties', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async updateBounty(
    id: string,
    data: Partial<CreateBountyForm>,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Bounty>> {
    return this.request<Bounty>(`/bounties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async deleteBounty(
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/bounties/${id}`, {
      method: 'DELETE',
      getToken,
    });
  }

  async getSponsorBounties(
    sponsorId: string = 'me',
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Bounty[]>> {
    return this.request<Bounty[]>(`/bounties/sponsor/${sponsorId}`, { getToken });
  }

  async getBountySubmissions(
    bountyId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/bounties/${bountyId}/submissions`, { getToken });
  }

  // Team Assignment endpoints
  async getTeamAssignments(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/teams', { getToken });
  }

  async submitProjectForTeam(
    teamId: string,
    data: {
      projectTitle?: string;
      githubRepoLink: string;
      demoDescription?: string;
      demoUrl?: string;
      slidesUrl?: string;
      videoUrl?: string;
      techStack?: string;
      bountyIds?: string[];
    },
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/teams/${teamId}/submit-project`, {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async getTeamAssignmentsByHackday(
    hackdayId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/teams/hackday/${hackdayId}`, { getToken });
  }

  async getTeamAssignmentsByParticipant(
    participantId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/teams/participant/${participantId}`, { getToken });
  }

  async getTeamAssignment(
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/teams/assignment/${id}`, { getToken });
  }

  async createTeamAssignment(
    data: {
      teamName: string;
      participantId: string;
      hackdayId: string;
      status?: 'Solo';
      teamMembers?: string[];
      lookingForSkills?: string[];
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/teams', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async updateTeamAssignment(
    id: string,
    data: {
      teamName?: string;
      status?: 'Solo';
      teamMembers?: string[];
      lookingForSkills?: string[];
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/teams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async deleteTeamAssignment(
    id: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/teams/${id}`, {
      method: 'DELETE',
      getToken,
    });
  }

  async getTeamStatistics(
    hackdayId?: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    const endpoint = hackdayId ? `/teams/stats/${hackdayId}` : '/teams/stats';
    return this.request<any>(endpoint, { getToken });
  }

  // Analytics endpoints
  async getAnalyticsOverview(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    activeHackdays: { count: number; success: boolean };
    totalAttendees: { count: number; breakdown: any[]; success: boolean };
    soloAttendees: { count: number; success: boolean };
  }>> {
    return this.request('/platformoverview/overview', { getToken });
  }

  async getActiveHackdaysCount(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{ count: number }>> {
    return this.request('/platformoverview/active-hackdays', { getToken });
  }

  async getTotalAttendeesCount(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{ count: number; breakdown: any[] }>> {
    return this.request('/platformoverview/total-attendees', { getToken });
  }

  async getSoloAttendeesCount(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{ count: number }>> {
    return this.request('/platformoverview/solo-attendees', { getToken });
  }

  async getCurrentEventsStats(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    creators: Array<{
      creatorId: string;
      creatorName: string;
      numberOfEvents: number;
      eventNames: string[];
      bountyParticipationPercentage: number;
      totalBounties: number;
      bountiesWithParticipants: number;
    }>;
    totalCreators: number;
    totalEvents: number;
  }>> {
    return this.request('/currentevents/stats', { getToken });
  }

  async getCurrentAttendees(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    attendees: Array<{
      id: string;
      name: string;
      email: string;
      linkedin: string;
      phone: string;
      telegram: string;
      roles: string[];
      skills: string[];
      hackdayCount: number;
      hackdayNames: string[];
      bountiesParticipated: number;
    }>;
    totalAttendees: number;
    totalEvents: number;
  }>> {
    return this.request('/currentattendees/stats', { getToken });
  }

  async getCurrentSponsors(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    sponsors: Array<{
      sponsorId: string;
      sponsorName: string;
      sponsorEmail: string;
      sponsorLinkedIn: string;
      numberOfEvents: number;
      eventNames: string[];
      totalBounties: number;
      activeBounties: number;
      totalParticipants: number;
    }>;
    totalSponsors: number;
    totalEvents: number;
  }>> {
    return this.request('/currentsponsors/stats', { getToken });
  }

  // Team Management endpoints
  async getParticipantsWithTeamStatus(
    sponsorId: string,
    hackdayId?: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    participants: Array<{
      id: string;
      name: string;
      email: string;
      linkedin: string;
      teamStatus: string;
      teamName: string;
      hackdayId: string;
      hackdayName: string;
      skills: string[];
    }>;
    hackdays: Array<{
      id: string;
      title: string;
    }>;
  }>> {
    const endpoint = hackdayId
      ? `/teammanagement/participants/${sponsorId}?hackdayId=${hackdayId}`
      : `/teammanagement/participants/${sponsorId}`;
    return this.request(endpoint, { getToken });
  }

  async createTeamFromParticipants(
    data: {
      teamName: string;
      hackdayId: string;
      participantIds: string[];
      teamLeaderId: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/teammanagement/create-team', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async getTeamsWithMembers(
    sponsorId: string,
    hackdayId?: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    teams: Array<{
      id: string;
      teamName: string;
      hackdayId: string;
      hackdayName: string;
      members: Array<{
        id: string;
        name: string;
        email: string;
        linkedin: string;
        skills: string[];
        isLeader: boolean;
      }>;
    }>;
    lookingForTeam: Array<{
      id: string;
      teamAssignmentId: string;
      name: string;
      email: string;
      linkedin: string;
      skills: string[];
      lookingForSkills?: string[];
      hackdayId: string;
      hackdayName: string;
    }>;
    soloParticipants: Array<{
      id: string;
      teamAssignmentId: string;
      name: string;
      email: string;
      linkedin: string;
      skills: string[];
      hackdayId: string;
      hackdayName: string;
    }>;
    hackdays: Array<{
      id: string;
      title: string;
    }>;
  }>> {
    const endpoint = hackdayId
      ? `/teammanagement/teams/${sponsorId}?hackdayId=${hackdayId}`
      : `/teammanagement/teams/${sponsorId}`;
    return this.request(endpoint, { getToken });
  }

  async moveParticipantToTeam(
    data: {
      participantId: string;
      fromTeamId: string | null;
      toTeamId: string;
      hackdayId: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>('/teammanagement/move-to-team', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async moveParticipantToSolo(
    data: {
      participantId: string;
      fromTeamId: string;
      hackdayId: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>('/teammanagement/move-to-solo', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async updateStatusToLooking(
    data: {
      participantId: string;
      teamAssignmentId: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>('/teammanagement/update-status-looking', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async updateStatusToSolo(
    data: {
      participantId: string;
      teamAssignmentId: string;
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>('/teammanagement/update-status-solo', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  // Current Projects endpoints
  async getCurrentProjects(
    sponsorId: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    projects: Array<{
      id: string;
      title: string;
      description: string;
      theme: string;
      status: string;
      statusColor: string;
      startDate: Date | string;
      endDate: Date | string;
      registrationDeadline: Date | string;
      maxParticipants: number;
      currentParticipants: number;
      totalBounties: number;
      activeBounties: number;
      totalTeams: number;
      soloParticipants: number;
      teamsWithMembers: number;
    }>;
    totalProjects: number;
  }>> {
    return this.request(`/currentprojects/${sponsorId}`, { getToken });
  }

  // Sponsor Next Event endpoints
  async getSponsorNextEvent(
    sponsorId: string = 'me',
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    id: string;
    title: string;
    description: string;
    theme: string;
    status: string;
    startDate: Date | string;
    endDate: Date | string;
    registrationDeadline: Date | string;
    maxParticipants: number;
    currentParticipants: number;
    eventLink: string;
    organizingCode: string;
    rules: string;
    type: string;
    createdBy: any;
    bounties: Array<{
      id: string;
      title: string;
      description: string;
      value: number;
      currency: string;
      status: string;
      type: string;
      difficulty: string;
      requirements: string[];
      tags: string[];
      maxWinners: number;
      currentWinners: number;
      startDate: Date | string;
      endDate: Date | string;
      participants: string[];
    }>;
    totalBounties: number;
    activeBounties: number;
  } | null>> {
    return this.request(`/sponsor-next-event/${sponsorId}`, { getToken, cacheTTL: CACHE_TTL.SHORT });
  }

  // Current Sponsor Bounties endpoints
  async getCurrentSponsorBounties(
    sponsorId: string = 'me',
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    bounties: Array<{
      id: string;
      title: string;
      description: string;
      value: number;
      currency: string;
      status: string;
      type: string;
      difficulty: string;
      requirements: string[];
      tags: string[];
      maxWinners: number;
      currentWinners: number;
      startDate: Date | string;
      endDate: Date | string;
      participants: string[];
      hackdays: Array<{
        id: string;
        title: string;
      }>;
      createdAt: Date | string;
      updatedAt: Date | string;
    }>;
    totalBounties: number;
    activeBounties: number;
    completedBounties: number;
    draftBounties: number;
  }>> {
    return this.request(`/current-sponsor-bounties/${sponsorId}`, { getToken });
  }

  // Current Events and Bounties endpoints
  async getCurrentEventsBounties(
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{
    events: Array<{
      id: string;
      title: string;
      description: string;
      theme: string;
      status: string;
      startDate: Date | string;
      endDate: Date | string;
      registrationDeadline: Date | string;
      maxParticipants: number;
      currentParticipants: number;
      eventLink: string;
      organizingCode: string;
      rules: string;
      type: string;
      createdBy: string;
      bounties: Array<{
        id: string;
        title: string;
        description: string;
        value: number;
        currency: string;
        status: string;
        type: string;
        difficulty: string;
        requirements: string[];
        tags: string[];
        maxWinners: number;
        currentWinners: number;
        startDate: Date | string;
        endDate: Date | string;
        participants: string[];
      }>;
      totalBounties: number;
      activeBounties: number;
    }>;
    totalEvents: number;
    totalBounties: number;
  }>> {
    return this.request('/current-events-bounties', { getToken });
  }

  async getCurrentEventsBountiesWithParticipation(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{
    events: Array<{
      id: string;
      title: string;
      description: string;
      theme: string;
      status: string;
      startDate: Date | string;
      endDate: Date | string;
      registrationDeadline: Date | string;
      maxParticipants: number;
      currentParticipants: number;
      eventLink: string;
      organizingCode: string;
      rules: string;
      type: string;
      createdBy: string;
      isRegistered: boolean;
      bounties: Array<{
        id: string;
        title: string;
        description: string;
        value: number;
        currency: string;
        status: string;
        type: string;
        difficulty: string;
        requirements: string[];
        tags: string[];
        maxWinners: number;
        currentWinners: number;
        startDate: Date | string;
        endDate: Date | string;
        participants: string[];
        // isRegistered field removed - bounty selection now happens during submission
      }>;
      totalBounties: number;
      activeBounties: number;
    }>;
    totalEvents: number;
    totalBounties: number;
  }>> {
    return this.request('/current-events-bounties/with-participation', {
      getToken,
      cacheTTL: CACHE_TTL.SHORT  // Shorter cache for user-specific data
    });
  }

  async registerForEvent(
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/current-events-bounties/${eventId}/register`, {
      method: 'POST',
      getToken,
    });
  }


  // User Events and Bounties endpoints
  async getUserEventsBounties(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{
    events: Array<{
      id: string;
      title: string;
      description: string;
      theme: string;
      status: string;
      startDate: Date | string;
      endDate: Date | string;
      registrationDeadline: Date | string;
      maxParticipants: number;
      currentParticipants: number;
      eventLink: string;
      organizingCode: string;
      rules: string;
      type: string;
      createdBy: string;
      bounties: Array<{
        id: string;
        title: string;
        description: string;
        value: number;
        currency: string;
        status: string;
        type: string;
        difficulty: string;
        requirements: string[];
        tags: string[];
        maxWinners: number;
        currentWinners: number;
        startDate: Date | string;
        endDate: Date | string;
        participants: string[];
      }>;
      totalBounties: number;
      activeBounties: number;
    }>;
    totalEvents: number;
    totalBounties: number;
  }>> {
    return this.request('/user-events-bounties', { getToken });
  }

  // Team Registration endpoints
  async createTeamRegistration(
    data: {
      teamName: string;
      eventId: string;
      registrationType: 'team' | 'solo' | 'matchmaking';
      lookingForTeam?: boolean;
      lookingForSkills?: string[];
      teamMemberEmails?: string[];
      status?: 'Solo' | 'Looking for a Team' | 'Has Team';
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/team-registration', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async updateTeamRegistration(
    teamId: string,
    data: {
      teamName?: string;
      lookingForSkills?: string[];
      teamMemberEmails?: string[];
      status?: 'Solo' | 'Looking for a Team' | 'Has Team';
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/team-registration/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async getEventTeams(
    eventId: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/team-registration/event/${eventId}`, { getToken });
  }

  async checkUserTeamForEvent(
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{ hasTeam: boolean; team: any | null }>> {
    return this.request<{ hasTeam: boolean; team: any | null }>(
      `/team-registration/check/${eventId}`,
      { getToken }
    );
  }

  async resignFromEvent(
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/team-registration/${eventId}/resign`, {
      method: 'DELETE',
      getToken,
    });
  }

  async searchParticipants(
    query: string,
    eventId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Array<{ id: string; name: string; email: string }>>> {
    return this.request<Array<{ id: string; name: string; email: string }>>(
      `/users/search/autocomplete?query=${encodeURIComponent(query)}&eventId=${encodeURIComponent(eventId)}`,
      { getToken }
    );
  }

  async getUserById(
    userId: string,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<{ id: string; name: string; email: string }>> {
    return this.request<{ id: string; name: string; email: string }>(`/users/${userId}`, {
      getToken,
    });
  }

  // Notification endpoints
  async getNotifications(
    page: number = 1,
    limit: number = 50,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Notification[]>> {
    const offset = (page - 1) * limit;
    return this.request<Notification[]>(
      `/notifications?limit=${limit}&offset=${offset}`,
      { getToken, cacheTTL: CACHE_TTL.SHORT }
    );
  }

  async getUnreadNotifications(
    limit: number = 50,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Notification[]>> {
    return this.request<Notification[]>(
      `/notifications/unread?limit=${limit}`,
      { getToken, cacheTTL: CACHE_TTL.SHORT }
    );
  }

  async getUnreadNotificationsCount(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{ count: number }>> {
    return this.request<{ count: number }>(
      `/notifications/unread/count`,
      { getToken, cacheTTL: CACHE_TTL.SHORT }
    );
  }

  async markNotificationAsRead(
    notificationId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<Notification>> {
    return this.request<Notification>(`/notifications/${notificationId}/read`, {
      method: 'PUT',
      getToken,
    });
  }

  async markAllNotificationsAsRead(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/notifications/read-all`, {
      method: 'PUT',
      getToken,
    });
  }

  async deleteNotification(
    notificationId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<void>> {
    return this.request<void>(`/notifications/${notificationId}`, {
      method: 'DELETE',
      getToken,
    });
  }

  async sendBroadcastMessage(
    hackdayId: string,
    title: string,
    message: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{ count: number }>> {
    return this.request<{ count: number }>(`/notifications/broadcast`, {
      method: 'POST',
      body: JSON.stringify({ hackdayId, title, message }),
      getToken,
    });
  }

  // Stripe Connect endpoints
  async createConnectedAccount(
    data: { country?: string; accountType?: 'express' | 'standard' },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/connect/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async getConnectedAccount(
    accountId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/connect/accounts/${accountId}`, { getToken });
  }

  async getCurrentUserConnectedAccount(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/connect/accounts/me/current', { getToken });
  }

  async createAccountLink(
    data: { accountId: string; refreshUrl: string; returnUrl: string },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{ url: string }>> {
    return this.request<{ url: string }>('/connect/account-links', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async getAccountBalance(
    accountId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/connect/balance/${accountId}`, { getToken });
  }

  async syncAccountStatus(
    accountId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/connect/sync/${accountId}`, {
      method: 'POST',
      getToken,
    });
  }

  async updateAccountSettings(
    accountId: string,
    data: { payoutSchedule?: string },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/connect/accounts/${accountId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
      getToken,
    });
  }

  // Stripe Payouts endpoints
  async createPayout(
    data: {
      connectedAccountId: string;
      amount: number;
      currency?: string;
      bountyId?: string;
      hackdayId?: string;
      description?: string;
      statementDescriptor?: string;
      metadata?: any;
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/payouts', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async createBountyPayout(
    data: {
      recipientUserId: string;
      amount: number;
      currency?: string;
      bountyId: string;
      hackdayId?: string;
      description?: string;
      statementDescriptor?: string;
      metadata?: any;
    },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/payouts/bounty', {
      method: 'POST',
      body: JSON.stringify(data),
      getToken,
    });
  }

  async getPayout(
    payoutId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/payouts/${payoutId}`, { getToken });
  }

  async getPayouts(
    params: { page?: number; pageSize?: number; status?: string; bountyId?: string; hackdayId?: string },
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{
    data: any[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }>> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });
    const endpoint = `/payouts?${queryParams.toString()}`;
    return this.request(endpoint, { getToken });
  }

  async cancelPayout(
    payoutId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/payouts/${payoutId}/cancel`, {
      method: 'POST',
      getToken,
    });
  }

  async getUserPayoutHistory(
    limit?: number,
    getToken?: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    const endpoint = limit ? `/payouts/me/history?limit=${limit}` : '/payouts/me/history';
    return this.request<any[]>(endpoint, { getToken });
  }

  async getUserEarnings(
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<{
    totalEarnings: number;
    totalPayouts: number;
    pendingPayouts: number;
    currency: string;
  }>> {
    return this.request('/payouts/me/earnings', { getToken });
  }

  async getBountyPayouts(
    bountyId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/payouts/bounty/${bountyId}`, { getToken });
  }

  async getHackdayPayouts(
    hackdayId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/payouts/hackday/${hackdayId}`, { getToken });
  }

  async syncPayoutStatus(
    payoutId: string,
    getToken: () => Promise<string | null>
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/payouts/${payoutId}/sync`, {
      method: 'POST',
      getToken,
    });
  }
}

// Create API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// Custom hook for API operations with authentication
export const useApiClient = () => {
  const { getToken } = useAuth();

  return useMemo(() => ({
    // Hackday operations
    getHackdays: (filters?: HackdayFilters & { page?: number; limit?: number }) =>
      apiClient.getHackdays(filters, getToken),

    getHackday: (id: string) =>
      apiClient.getHackday(id, getToken),

    createHackday: (data: CreateHackdayForm) =>
      apiClient.createHackday(data, getToken),

    updateHackday: (id: string, data: Partial<CreateHackdayForm>) =>
      apiClient.updateHackday(id, data, getToken),

    deleteHackday: (id: string) =>
      apiClient.deleteHackday(id, getToken),

    getSponsorHackdays: (sponsorId: string = 'me') =>
      apiClient.getSponsorHackdays(sponsorId, getToken),

    // User operations
    getCurrentUser: () =>
      apiClient.getCurrentUser(getToken),

    updateCurrentUser: (data: { username?: string; role?: 'sponsor' | 'participant' }) =>
      apiClient.updateCurrentUser(data, getToken),

    getUserStats: () =>
      apiClient.getUserStats(getToken),

    getUserActivity: (page: number = 1, limit: number = 10) =>
      apiClient.getUserActivity(page, limit, getToken),

    // Bounty operations
    getBounties: (filters?: any & { page?: number; limit?: number }) =>
      apiClient.getBounties(filters, getToken),

    getBounty: (id: string) =>
      apiClient.getBounty(id, getToken),

    getHackdayBounties: (hackdayId: string) =>
      apiClient.getHackdayBounties(hackdayId, getToken),

    createBounty: (data: CreateBountyForm) =>
      apiClient.createBounty(data, getToken),

    updateBounty: (id: string, data: Partial<CreateBountyForm>) =>
      apiClient.updateBounty(id, data, getToken),

    deleteBounty: (id: string) =>
      apiClient.deleteBounty(id, getToken),

    getSponsorBounties: (sponsorId?: string) =>
      apiClient.getSponsorBounties(sponsorId, getToken),

    getBountySubmissions: (bountyId: string) =>
      apiClient.getBountySubmissions(bountyId, getToken),

    // Team Assignment operations
    getTeamAssignments: () =>
      apiClient.getTeamAssignments(getToken),

    getTeamAssignmentsByHackday: (hackdayId: string) =>
      apiClient.getTeamAssignmentsByHackday(hackdayId, getToken),

    getTeamAssignmentsByParticipant: (participantId: string) =>
      apiClient.getTeamAssignmentsByParticipant(participantId, getToken),

    getTeamAssignment: (id: string) =>
      apiClient.getTeamAssignment(id, getToken),

    createTeamAssignment: (data: {
      teamName: string;
      participantId: string;
      hackdayId: string;
      status?: 'Solo';
      teamMembers?: string[];
      lookingForSkills?: string[];
    }) =>
      apiClient.createTeamAssignment(data, getToken),

    updateTeamAssignment: (id: string, data: {
      teamName?: string;
      status?: 'Solo';
      teamMembers?: string[];
      lookingForSkills?: string[];
    }) =>
      apiClient.updateTeamAssignment(id, data, getToken),

    deleteTeamAssignment: (id: string) =>
      apiClient.deleteTeamAssignment(id, getToken),

    getTeamStatistics: (hackdayId?: string) =>
      apiClient.getTeamStatistics(hackdayId, getToken),

    submitProjectForTeam: (teamId: string, data: any) =>
      apiClient.submitProjectForTeam(teamId, data, getToken),

    // Analytics operations
    getAnalyticsOverview: () =>
      apiClient.getAnalyticsOverview(getToken),

    getActiveHackdaysCount: () =>
      apiClient.getActiveHackdaysCount(getToken),

    getTotalAttendeesCount: () =>
      apiClient.getTotalAttendeesCount(getToken),

    getSoloAttendeesCount: () =>
      apiClient.getSoloAttendeesCount(getToken),

    getCurrentEventsStats: () =>
      apiClient.getCurrentEventsStats(getToken),

    getCurrentAttendees: () =>
      apiClient.getCurrentAttendees(getToken),

    getCurrentSponsors: () =>
      apiClient.getCurrentSponsors(getToken),

    // Team Management operations
    getParticipantsWithTeamStatus: (sponsorId: string, hackdayId?: string) =>
      apiClient.getParticipantsWithTeamStatus(sponsorId, hackdayId, getToken),

    createTeamFromParticipants: (data: {
      teamName: string;
      hackdayId: string;
      participantIds: string[];
      teamLeaderId: string;
    }) =>
      apiClient.createTeamFromParticipants(data, getToken),

    getTeamsWithMembers: (sponsorId: string, hackdayId?: string) =>
      apiClient.getTeamsWithMembers(sponsorId, hackdayId, getToken),

    moveParticipantToTeam: (data: {
      participantId: string;
      fromTeamId: string | null;
      toTeamId: string;
      hackdayId: string;
    }) =>
      apiClient.moveParticipantToTeam(data, getToken),

    moveParticipantToSolo: (data: {
      participantId: string;
      fromTeamId: string;
      hackdayId: string;
    }) =>
      apiClient.moveParticipantToSolo(data, getToken),

    updateStatusToLooking: (data: {
      participantId: string;
      teamAssignmentId: string;
    }) =>
      apiClient.updateStatusToLooking(data, getToken),

    updateStatusToSolo: (data: {
      participantId: string;
      teamAssignmentId: string;
    }) =>
      apiClient.updateStatusToSolo(data, getToken),

    // Current Projects operations
    getCurrentProjects: (sponsorId: string) =>
      apiClient.getCurrentProjects(sponsorId, getToken),

    // Sponsor Next Event operations
    getSponsorNextEvent: (sponsorId: string = 'me') =>
      apiClient.getSponsorNextEvent(sponsorId, getToken),

    // Current Sponsor Bounties operations
    getCurrentSponsorBounties: (sponsorId: string = 'me') =>
      apiClient.getCurrentSponsorBounties(sponsorId, getToken),

    // Current Events and Bounties operations
    getCurrentEventsBounties: () =>
      apiClient.getCurrentEventsBounties(getToken),

    getCurrentEventsBountiesWithParticipation: () =>
      apiClient.getCurrentEventsBountiesWithParticipation(getToken),

    registerForEvent: (eventId: string) =>
      apiClient.registerForEvent(eventId, getToken),

    // User Events and Bounties operations
    getUserEventsBounties: () =>
      apiClient.getUserEventsBounties(getToken),

    // Team Registration operations
    createTeamRegistration: (data: {
      teamName: string;
      eventId: string;
      registrationType: 'team' | 'solo' | 'matchmaking';
      lookingForTeam?: boolean;
      lookingForSkills?: string[];
      teamMemberEmails?: string[];
      status?: 'Solo' | 'Looking for a Team' | 'Has Team';
    }) =>
      apiClient.createTeamRegistration(data, getToken),

    updateTeamRegistration: (teamId: string, data: {
      teamName?: string;
      lookingForSkills?: string[];
      teamMemberEmails?: string[];
      status?: 'Solo' | 'Looking for a Team' | 'Has Team';
    }) =>
      apiClient.updateTeamRegistration(teamId, data, getToken),

    getEventTeams: (eventId: string) =>
      apiClient.getEventTeams(eventId, getToken),

    checkUserTeamForEvent: (eventId: string) =>
      apiClient.checkUserTeamForEvent(eventId, getToken),

    resignFromEvent: (eventId: string) =>
      apiClient.resignFromEvent(eventId, getToken),
    searchParticipants: (query: string, eventId: string) =>
      apiClient.searchParticipants(query, eventId, getToken),

    getUserById: (userId: string) =>
      apiClient.getUserById(userId, getToken),

    // Notification operations
    getNotifications: (page: number = 1, limit: number = 50) =>
      apiClient.getNotifications(page, limit, getToken),

    getUnreadNotifications: (limit: number = 50) =>
      apiClient.getUnreadNotifications(limit, getToken),

    getUnreadNotificationsCount: () =>
      apiClient.getUnreadNotificationsCount(getToken),

    markNotificationAsRead: (notificationId: string) =>
      apiClient.markNotificationAsRead(notificationId, getToken),

    markAllNotificationsAsRead: () =>
      apiClient.markAllNotificationsAsRead(getToken),

    deleteNotification: (notificationId: string) =>
      apiClient.deleteNotification(notificationId, getToken),

    sendBroadcastMessage: (hackdayId: string, title: string, message: string) =>
      apiClient.sendBroadcastMessage(hackdayId, title, message, getToken),

    // Stripe Connect operations
    createConnectedAccount: (data: { country?: string; accountType?: 'express' | 'standard' }) =>
      apiClient.createConnectedAccount(data, getToken),

    getConnectedAccount: (accountId: string) =>
      apiClient.getConnectedAccount(accountId, getToken),

    getCurrentUserConnectedAccount: () =>
      apiClient.getCurrentUserConnectedAccount(getToken),

    createAccountLink: (data: { accountId: string; refreshUrl: string; returnUrl: string }) =>
      apiClient.createAccountLink(data, getToken),

    getAccountBalance: (accountId: string) =>
      apiClient.getAccountBalance(accountId, getToken),

    syncAccountStatus: (accountId: string) =>
      apiClient.syncAccountStatus(accountId, getToken),

    updateAccountSettings: (accountId: string, data: { payoutSchedule?: string }) =>
      apiClient.updateAccountSettings(accountId, data, getToken),

    // Stripe Payouts operations
    createPayout: (data: {
      connectedAccountId: string;
      amount: number;
      currency?: string;
      bountyId?: string;
      hackdayId?: string;
      description?: string;
      statementDescriptor?: string;
      metadata?: any;
    }) =>
      apiClient.createPayout(data, getToken),

    createBountyPayout: (data: {
      recipientUserId: string;
      amount: number;
      currency?: string;
      bountyId: string;
      hackdayId?: string;
      description?: string;
      statementDescriptor?: string;
      metadata?: any;
    }) =>
      apiClient.createBountyPayout(data, getToken),

    getPayout: (payoutId: string) =>
      apiClient.getPayout(payoutId, getToken),

    getPayouts: (params: { page?: number; pageSize?: number; status?: string; bountyId?: string; hackdayId?: string }) =>
      apiClient.getPayouts(params, getToken),

    cancelPayout: (payoutId: string) =>
      apiClient.cancelPayout(payoutId, getToken),

    getUserPayoutHistory: (limit?: number) =>
      apiClient.getUserPayoutHistory(limit, getToken),

    getUserEarnings: () =>
      apiClient.getUserEarnings(getToken),

    getBountyPayouts: (bountyId: string) =>
      apiClient.getBountyPayouts(bountyId, getToken),

    getHackdayPayouts: (hackdayId: string) =>
      apiClient.getHackdayPayouts(hackdayId, getToken),

    syncPayoutStatus: (payoutId: string) =>
      apiClient.syncPayoutStatus(payoutId, getToken),

    // Cache management operations
    clearCache: () =>
      apiClient.clearCache(),

    invalidateCache: (pattern: string) =>
      apiClient.invalidateCache(pattern),
  }), [getToken]);
};
